import { useMemo } from "react"
import { addDays } from "date-fns"

import { api } from "~/utils/api"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { PERF_WEEKDIFF_MARGIN } from "~/hooks/usePerformanceData"
import { projectPerfStats } from "~/components/demand-2/shared/perfProjection"

export const LOOKBACK_DAYS = 730

// Every tab passes these same windows so the heavy `getPerformanceStats` query shares one cache
// entry across the Collections and Performance tabs. Windows are cheap FILTER aggregates.
export const DEMAND2_PERF_WINDOWS = [4, 12]

export type VariantPerfStats = {
  totalExpectedDemand: number
  /** Gross units: returns are not subtracted. */
  totalSales: number
  /** Dated by return processing, so a period can carry returns of sales outside it. */
  totalReturns: number
  /** Already net of returns (see DailySale.revenue). */
  totalRevenue: number
  /** Gross units × current Sku.costPrice; missing cost prices count as 0. */
  totalCost: number
  overlapPeriods: number
  /** Null on the per-period bundles and on the channel-filtered branch. */
  firstWeek: string | null
  lastWeek: string | null
  /** Null on the per-period bundles. */
  basis: "plan" | "model" | null
}

export type Demand2PerformanceSummary = {
  totalExpectedDemand: number
  totalSales: number
  totalReturns: number
  totalRevenue: number
  totalCost: number
}

export type Demand2WindowStats = {
  statsByVariant: Map<string, VariantPerfStats>
  summary: Demand2PerformanceSummary
}

/**
 * Per-variant expected vs actual over the overlapping weeks, plus summary totals. Shared by the
 * Performance tab and the collection Overview so both read identical numbers.
 */
export function useDemand2Performance({
  collectionIds,
  displayVariantIds,
  windows,
  dateRanges,
  warehouseId,
  channel,
  enabled: callerEnabled = true,
}: {
  collectionIds: number[]
  displayVariantIds: string[]
  windows?: number[]
  dateRanges?: { key: string; from: Date; to: Date }[]
  warehouseId?: string
  channel?: string
  /** For callers whose `dateRanges` aren't final yet. */
  enabled?: boolean
}) {
  const enabled = callerEnabled && collectionIds.length > 0 && displayVariantIds.length > 0
  const statsQ = api.demand2.getPerformanceStats.useQuery(
    {
      collectionIds,
      fromDate: addDays(getStartOfCurrentWeekUTC(), -(LOOKBACK_DAYS + PERF_WEEKDIFF_MARGIN * 7)),
      lookbackDays: LOOKBACK_DAYS,
      windows: windows ?? [],
      dateRanges: dateRanges ?? [],
      warehouseId,
      channel,
    },
    // Keeps the previous stats on screen while a filter change refetches.
    { enabled, placeholderData: (prev) => prev },
  )

  // `windows` must be a module constant (DEMAND2_PERF_WINDOWS) or undefined: an inline array
  // would recompute per render.
  const {
    statsByVariant,
    summary,
    byWindow,
    dateRanges: dateRangesStats,
    lookback,
  } = useMemo(
    () => projectPerfStats(statsQ.data ?? [], displayVariantIds, windows),
    [statsQ.data, displayVariantIds, windows],
  )

  return {
    statsByVariant,
    summary,
    byWindow,
    dateRangesStats,
    lookback,
    isLoading: statsQ.isLoading,
    // True while refetching behind placeholder data: the numbers are from the previous periods.
    isFetching: statsQ.isFetching,
  }
}
